import type { DiscountType, QuotationParameters } from '../types';

/**
 * #77 — the customer discount, resolved to shekels.
 *
 * Where it sits in the chain (confirmed with the requester):
 *
 *   subtotal → markup → customerPrice → × (1 + risk%) → totalQuote
 *     → − discount = netQuote → × (1 + VAT%) → finalTotal
 *
 * After risk, before VAT. That is the ordinary commercial reading: the discount
 * is a line on the quotation, and VAT is charged on what the customer actually
 * pays. Applying it before risk would shrink the risk contribution along with
 * the discount, and applying it after VAT would leave the printed VAT figure
 * disagreeing with the amount being charged.
 */

/** The parameter fields this module reads. */
export type DiscountSource = Pick<
  QuotationParameters,
  'discountType' | 'discountValue'
>;

/** True when this quotation carries a discount that actually reduces the price. */
export function hasDiscount(parameters: DiscountSource | undefined): boolean {
  return resolveDiscountValue(parameters) !== null;
}

/**
 * Normalise the type/value pair, or null when there is no usable discount.
 *
 * A value of zero returns null rather than a zero discount, so "no discount"
 * and "a discount of nothing" are the same state everywhere downstream — there
 * is no way to end up with a discount line on a quotation that takes nothing
 * off it.
 */
function resolveDiscountValue(
  parameters: DiscountSource | undefined
): { type: DiscountType; value: number } | null {
  if (!parameters) return null;

  const { discountType: type, discountValue: value } = parameters;
  if (type !== 'percent' && type !== 'amount') return null;
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (value <= 0) return null;

  return { type, value };
}

/**
 * How many shekels come off `base`.
 *
 * **Clamped to `base`.** A discount larger than the price would otherwise make
 * the total negative, which is not a quotation — it is an invoice pointing the
 * wrong way. Note what this clamp is and is not: it is arithmetic, not policy.
 * The requester was explicit that a sales user may give any discount they like,
 * so 150% is accepted and lands the customer at zero rather than being rejected
 * as too large.
 *
 * @param base - the pre-VAT total the discount applies to (`totalQuoteILS`)
 * @param parameters - the quotation's discount type and value
 * @returns shekels to subtract; 0 when no discount applies
 */
export function resolveDiscountILS(
  base: number,
  parameters: DiscountSource | undefined
): number {
  if (!Number.isFinite(base) || base <= 0) return 0;

  const discount = resolveDiscountValue(parameters);
  if (!discount) return 0;

  const raw =
    discount.type === 'percent'
      ? base * (discount.value / 100)
      : discount.value;

  return Math.min(raw, base);
}

/**
 * Render a discount for display — "10%" or "₪5,000".
 *
 * @param parameters - the quotation's discount type and value
 * @returns the label, or undefined when no discount is set
 */
export function formatDiscount(
  parameters: DiscountSource | undefined
): string | undefined {
  const discount = resolveDiscountValue(parameters);
  if (!discount) return undefined;

  if (discount.type === 'percent') {
    return `${Number(discount.value.toFixed(2))}%`;
  }

  return `₪${discount.value.toLocaleString('he-IL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}
